export interface PriceTier {
  minUnits: number;
  maxUnits?: number | null;
  pricePerUnit: number;
}

export interface VolumeDiscount {
  minTotalUnits: number;
  discountPercentage: number;
}

export interface CapacityPack {
  capacity: number;
  price: number;
}

export interface EffectivePricingConfig {
  pricingModel: 'hybrid' | 'unit-based' | 'tiered' | 'plan-based';
  pricePerUnit: number;
  priceTiers?: PriceTier[];
  minPricePerClient?: number;
  volumeDiscounts?: VolumeDiscount[];
}

export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

/**
 * Preço por cliente/condomínio a partir das unidades contratadas: no modelo "hybrid" usa o maior
 * tier de `priceTiers` cujo `minUnits` seja <= às unidades escolhidas (senão o preço unitário
 * base); nos demais modelos usa sempre o preço unitário base. Nunca fica abaixo de
 * `minPricePerClient`.
 */
export const calculateClientPrice = (
  units: number,
  config: EffectivePricingConfig | undefined,
): number => {
  if (!config || units === 0) return 0;

  let calculatedPrice = 0;

  if (config.pricingModel === 'hybrid') {
    let unitPrice = config.pricePerUnit;
    if (config.priceTiers && config.priceTiers.length > 0) {
      const sortedTiers = [...config.priceTiers].sort((a, b) => b.minUnits - a.minUnits);
      for (const tier of sortedTiers) {
        if (units >= tier.minUnits) {
          unitPrice = tier.pricePerUnit;
          break;
        }
      }
    }
    calculatedPrice = units * unitPrice;
  } else {
    calculatedPrice = units * config.pricePerUnit;
  }

  const minPrice = config.minPricePerClient || 0;
  return Math.max(calculatedPrice, minPrice);
};

export interface ClientPriceBreakdown {
  units: number;
  /** Preço final (com o mínimo por cliente já aplicado, quando aplicável). */
  price: number;
  /** Subtotal calculado antes de aplicar o mínimo por cliente. */
  subtotalBeforeMin: number;
  /** Quanto do mínimo foi efetivamente aplicado (0 quando o subtotal já superava o mínimo). */
  minimumApplied: number;
}

/**
 * Como `calculateClientPrice`, mas também expõe o subtotal antes do mínimo e o valor do mínimo
 * aplicado — necessário para calcular o desconto por volume sobre a parte variável (excluindo
 * mínimos), como faz a calculadora unificada.
 */
export const calculateClientPriceBreakdown = (
  units: number,
  config: EffectivePricingConfig | undefined,
): ClientPriceBreakdown => {
  let calculatedPrice = 0;
  if (config && units > 0) {
    if (config.pricingModel === 'hybrid') {
      let unitPrice = config.pricePerUnit;
      if (config.priceTiers && config.priceTiers.length > 0) {
        const sortedTiers = [...config.priceTiers].sort((a, b) => b.minUnits - a.minUnits);
        for (const tier of sortedTiers) {
          if (units >= tier.minUnits) {
            unitPrice = tier.pricePerUnit;
            break;
          }
        }
      }
      calculatedPrice = units * unitPrice;
    } else {
      calculatedPrice = units * config.pricePerUnit;
    }
  }
  const minPrice = config?.minPricePerClient || 0;
  const subtotalBeforeMin = calculatedPrice;
  const totalWithMin = Math.max(calculatedPrice, minPrice);
  const minimumApplied = subtotalBeforeMin < minPrice ? minPrice : 0;

  return { units, price: totalWithMin, subtotalBeforeMin, minimumApplied };
};

/**
 * Desconto por volume (planos Gestão/Institucional apenas — Condomínio não tem desconto
 * progressivo). Usa a maior faixa de `volumeDiscounts` cujo `minTotalUnits` seja <= ao total de
 * unidades.
 */
export const calculateVolumeDiscount = (
  totalUnits: number,
  subtotal: number,
  config: Pick<EffectivePricingConfig, 'volumeDiscounts'> | undefined,
  planAllowsDiscount: boolean,
): number => {
  if (!planAllowsDiscount) return 0;
  if (!config?.volumeDiscounts || config.volumeDiscounts.length === 0) {
    return 0;
  }

  const sortedDiscounts = [...config.volumeDiscounts].sort((a, b) => b.minTotalUnits - a.minTotalUnits);

  for (const discount of sortedDiscounts) {
    if (totalUnits >= discount.minTotalUnits) {
      return (subtotal * discount.discountPercentage) / 100;
    }
  }
  return 0;
};

/**
 * Custo por capacidade contratada (clientes/condomínios no plano Gestão, ou núcleos no plano
 * Institucional — mesma regra para os dois). Pacote exato usa o preço do pacote; quantidade fora
 * dos pacotes usa o preço unitário do maior pacote abaixo da quantidade escolhida (ou do menor
 * pacote disponível, se a quantidade for menor que todos).
 */
export const calculateCapacityCost = (count: number, packs: CapacityPack[] | undefined): number => {
  const list = packs ?? [];
  if (list.length === 0 || count <= 0) return 0;
  const sorted = [...list].sort((a, b) => a.capacity - b.capacity);
  const exact = sorted.find((p) => p.capacity === count);
  if (exact) return exact.price;
  const packBelow = sorted.filter((p) => p.capacity < count).pop();
  const base = packBelow ?? sorted[0];
  if (base == null) return 0;
  const unitPrice = base.price / base.capacity;
  return Math.round(count * unitPrice * 100) / 100;
};
